import { cn } from '@/lib/utils';
import { Building2, User, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

interface UBO {
  name: string;
  shareholding: number;
  type: 'individual' | 'company';
  registrationNumber?: string;
  ubos?: UBO[];
}

interface Shareholder {
  name: string;
  shareholding: number;
  shareClass: string;
  type: 'individual' | 'company';
  ubos?: UBO[];
  registrationNumber?: string;
}

interface Director {
  name: string;
  occupation: string;
}

interface OwnershipTreeProps {
  companyName: string;
  registrationNumber?: string;
  directors?: Director[];
  shareholders?: Shareholder[];
}

function TreeNode({ 
  name, 
  type, 
  shareholding, 
  registrationNumber,
  ubos, 
  level = 0,
  isLast = false
}: { 
  name: string; 
  type: 'individual' | 'company' | 'director'; 
  shareholding?: number;
  registrationNumber?: string;
  ubos?: UBO[];
  level?: number;
  isLast?: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = ubos && ubos.length > 0;
  const isCompany = type === 'company';
  const isDirector = type === 'director';

  return (
    <div className="relative">
      {/* Connector lines */}
      {level > 0 && (
        <>
          <div 
            className="absolute left-0 top-0 w-4 border-l-2 border-white/20"
            style={{ 
              height: isLast ? '20px' : '100%',
              marginLeft: `${(level - 1) * 24}px`
            }}
          />
          <div 
            className="absolute top-5 h-0.5 w-4 bg-white/20"
            style={{ left: `${(level - 1) * 24}px` }}
          />
        </>
      )}
      
      {/* Node */}
      <div 
        className={cn(
          "flex items-center gap-2 py-1.5 cursor-default",
          hasChildren && "cursor-pointer"
        )}
        style={{ paddingLeft: `${level * 24}px` }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        {hasChildren ? (
          expanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-white/50 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-white/50 flex-shrink-0" />
          )
        ) : (
          <div className="w-3.5" />
        )}
        
        <div className={cn(
          "flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0",
          isCompany ? "bg-accent/20" : isDirector ? "bg-blue-500/20" : "bg-success/20"
        )}>
          {isCompany ? (
            <Building2 className="w-3.5 h-3.5 text-accent" />
          ) : (
            <User className={cn("w-3.5 h-3.5", isDirector ? "text-blue-400" : "text-success")} />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">{name}</span>
            {shareholding !== undefined && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-white/10 text-white/70 flex-shrink-0">
                {shareholding}%
              </span>
            )}
          </div>
          {registrationNumber && (
            <span className="text-xs text-white/40">#{registrationNumber}</span>
          )}
        </div>
      </div>

      {/* Children */}
      {hasChildren && expanded && (
        <div>
          {ubos.map((ubo, idx) => (
            <TreeNode
              key={`${ubo.name}-${idx}`}
              name={ubo.name}
              type={ubo.type}
              shareholding={ubo.shareholding}
              registrationNumber={ubo.registrationNumber}
              ubos={ubo.ubos}
              level={level + 1}
              isLast={idx === ubos.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function OwnershipTree({ 
  companyName, 
  registrationNumber,
  directors = [], 
  shareholders = [] 
}: OwnershipTreeProps) {
  const [showDirectors, setShowDirectors] = useState(true);
  const [showShareholders, setShowShareholders] = useState(true);

  return (
    <div className="bg-white/5 backdrop-blur rounded-xl p-4 text-left">
      <h3 className="font-semibold mb-3 text-sm text-white/80">Ownership Structure</h3>
      
      {/* Root Company */}
      <div className="flex items-center gap-2 pb-2 mb-2 border-b border-white/10">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-accent/30">
          <Building2 className="w-4 h-4 text-accent" />
        </div>
        <div>
          <div className="font-semibold text-white text-sm">{companyName}</div>
          {registrationNumber && (
            <div className="text-xs text-white/40">#{registrationNumber}</div>
          )}
        </div>
      </div>

      {/* Directors Section */}
      {directors.length > 0 && (
        <div className="mb-3">
          <button 
            onClick={() => setShowDirectors(!showDirectors)}
            className="flex items-center gap-1.5 text-xs font-medium text-white/60 hover:text-white/80 mb-1.5"
          >
            {showDirectors ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Directors ({directors.length})
          </button>
          {showDirectors && (
            <div className="pl-2 border-l-2 border-blue-500/30">
              {directors.map((director, idx) => (
                <TreeNode
                  key={`director-${idx}`}
                  name={director.name}
                  type="director"
                  level={0}
                  isLast={idx === directors.length - 1}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Shareholders Section */}
      {shareholders.length > 0 && (
        <div>
          <button 
            onClick={() => setShowShareholders(!showShareholders)}
            className="flex items-center gap-1.5 text-xs font-medium text-white/60 hover:text-white/80 mb-1.5"
          >
            {showShareholders ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Shareholders ({shareholders.length})
          </button>
          {showShareholders && (
            <div className="pl-2 border-l-2 border-success/30">
              {shareholders.map((shareholder, idx) => (
                <TreeNode
                  key={`shareholder-${idx}`}
                  name={shareholder.name}
                  type={shareholder.type}
                  shareholding={shareholder.shareholding}
                  registrationNumber={shareholder.registrationNumber}
                  ubos={shareholder.ubos}
                  level={0}
                  isLast={idx === shareholders.length - 1}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/10">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-blue-400" />
          <span className="text-xs text-white/50">Director</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-success" />
          <span className="text-xs text-white/50">Individual</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-accent" />
          <span className="text-xs text-white/50">Company</span>
        </div>
      </div>
    </div>
  );
}
